import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { useCaptureException, ZBError } from '@zenbusiness/application-commons-logging';

import {
  ENABLE_BLR_SPECIFIC_CONTENT,
  ENABLE_PRODUCT_LANDING_PERSONALIZATION,
} from '#client/constants/experimentsFlags';
import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { VELO_BANNER_DEFAULT } from '#client/pages/ProductLanding/components/HeroSection/constants';
import { extractPersonalizationPayload } from '#client/pages/ProductLanding/personalization/extractPersonalizationPayload';
import { normalizePersonalizationResponse } from '#client/pages/ProductLanding/personalization/normalizePersonalizationResponse';
import { validatePersonalizationResponse } from '#client/pages/ProductLanding/personalization/validatePersonalizationResponse';
import { PRODUCTS_KEY } from '#client/pages/Products/constants';

import { useProductDetails } from './useProductDetails';

import { useBlrRepurchaseReadiness } from '../hooks/useBlrRepurchaseReadiness';
import { useFormationStatus } from '../hooks/useFormationStatus';
import { useProductLandingPersonalization } from '../hooks/useProductLandingPersonalization';
import { useTaxClassification } from '../hooks/useTaxClassification';

const ProductLandingContext = createContext();

const useProductLandingContext = () => {
  const context = useContext(ProductLandingContext);

  if (!context) {
    throw new ZBError('useProductLanding must be used within a ProductLandingContext.Provider');
  }

  return context;
};

const useProductLandingContextOptional = () => useContext(ProductLandingContext);

const resolveConfig = (config, context) => (typeof config === 'function' ? config(context) : config);

const ProductLandingProvider = ({ children, federated, productId }) => {
  const navigate = useNavigate();
  const { formatMessage, locale, messages: intlMessages } = useIntl();
  const productsPath = useDashboardPath(`/${PRODUCTS_KEY}`);
  const productConfig = useProductDetails(productId);
  const formationStatus = useFormationStatus();
  const { entitlements, businessEntityUuid } = useBusinessEntity();
  const { orderTasksWithStatus } = useOrderTrackerStatus();
  const captureException = useCaptureException();
  const [personalizedMessageMap, setPersonalizedMessageMap] = useState({});
  const [personalizationStatus, setPersonalizationStatus] = useState('disabled');
  const [showPersonalizationComplete, setShowPersonalizationComplete] = useState(false);
  const completionTimeoutRef = useRef(null);
  const { requestPersonalization } = useProductLandingPersonalization();

  const resetPersonalizationState = useCallback(() => {
    clearTimeout(completionTimeoutRef.current);
    setPersonalizationStatus('disabled');
    setShowPersonalizationComplete(false);
    setPersonalizedMessageMap((prev) => (Object.keys(prev).length > 0 ? {} : prev));
  }, []);

  const { flagKey, redirectPath } = productConfig?.productGates?.flagGate || {};
  const isFlagEnabled = useExperimentFlag(flagKey || '', false, false, !flagKey);
  const fallbackRedirectPath = useDashboardPath(redirectPath || `/${PRODUCTS_KEY}`);

  const isBusinessLicenseReportEnabled = useExperimentFlag(
    productId === 'business-licenses' ? ENABLE_BLR_SPECIFIC_CONTENT : '',
    false,
    false,
    productId !== 'business-licenses',
  );
  const businessLicenseReportPath = useDashboardPath(`/${PRODUCTS_KEY}/business-license-report`);

  const isProductLandingPersonalizationEnabled = useExperimentFlag(ENABLE_PRODUCT_LANDING_PERSONALIZATION, false);

  const { taxClassification } = useTaxClassification({
    skip: !productConfig?.isTaxClassificationDependent,
  });

  const blrRepurchaseReadiness = useBlrRepurchaseReadiness({
    enabled: productId === 'business-license-report',
  });
  const blrRepurchase = useMemo(
    () => ({
      isBlrRepurchaseEnabled: true,
      allBlrOrdersComplete: blrRepurchaseReadiness.allBlrOrdersComplete,
      loading: blrRepurchaseReadiness.loading,
    }),
    [blrRepurchaseReadiness.allBlrOrdersComplete, blrRepurchaseReadiness.loading],
  );

  useEffect(
    function redirects() {
      if (productId === 'business-licenses' && isBusinessLicenseReportEnabled) {
        navigate(businessLicenseReportPath, { replace: true });
        return;
      }
      if (flagKey && !isFlagEnabled) {
        navigate(fallbackRedirectPath, { replace: true });
        return;
      }
      if (!productConfig) {
        navigate(productsPath, { replace: true });
      }
    },
    [
      navigate,
      productsPath,
      productConfig,
      flagKey,
      isFlagEnabled,
      fallbackRedirectPath,
      productId,
      isBusinessLicenseReportEnabled,
      businessLicenseReportPath,
    ],
  );

  const heroConfig = useMemo(() => {
    const hero = productConfig?.hero;
    if (!hero) {
      return {};
    }

    return {
      packages: hero.buyBox || [],
      defaultPackageIndex: hero.defaultPackageIndex ?? 0,
      veloBanner: hero.veloBanner || VELO_BANNER_DEFAULT,
      comparePackages: hero.comparePackages,
      plans: hero.plans,
      getBuyButtonConfig:
        productConfig.getBuyButtonConfig ||
        (() => ({
          isBuyEnabled: false,
          ctaText: 'productPage.common.buyBox.buttonDisabled.label',
          ctaDisabledTooltip: 'productPage.common.buyBox.buttonDisabled.tooltip',
        })),
    };
  }, [productConfig]);

  const isFederated = Boolean(federated);

  const baseContext = useMemo(
    () => ({
      isFederated,
      productConfig,
      productId,
      formationStatus,
      entitlements,
      orderTasksWithStatus,
      taxClassification,
      blrRepurchase,
      ...heroConfig,
    }),
    [
      isFederated,
      productConfig,
      productId,
      formationStatus,
      entitlements,
      heroConfig,
      orderTasksWithStatus,
      taxClassification,
      blrRepurchase,
    ],
  );

  const resolvedProductConfig = useMemo(() => {
    if (!productConfig) {
      return productConfig;
    }

    const { hero, benefit, banner, footnotes, howItWorks, whatsIncluded } = productConfig;

    return {
      ...productConfig,
      banner: resolveConfig(banner, baseContext),
      benefit: resolveConfig(benefit, baseContext),
      footnotes: resolveConfig(footnotes, baseContext),
      hero: hero && {
        ...hero,
        subheader: resolveConfig(hero.subheader, baseContext),
      },
      howItWorks: howItWorks && {
        ...howItWorks,
        items: resolveConfig(howItWorks.items, baseContext),
      },
      whatsIncluded: whatsIncluded && {
        ...whatsIncluded,
        items: resolveConfig(whatsIncluded.items, baseContext),
      },
    };
  }, [productConfig, baseContext]);

  // Keep a ref so the effect can read the latest config without re-firing on every
  // config reference change (formationStatus returns a new object each render,
  // which would otherwise make resolvedProductConfig unstable and loop the effect).
  const resolvedProductConfigRef = useRef(resolvedProductConfig);
  resolvedProductConfigRef.current = resolvedProductConfig;

  // Inject personalized copy into the intl message store rather than replacing
  // message keys in the config — keeps the normal formatMessage path working for
  // all child components without triggering MissingTranslationErrors.
  const mergedMessages = useMemo(
    () => ({
      ...intlMessages,
      ...personalizedMessageMap,
    }),
    [intlMessages, personalizedMessageMap],
  );

  useEffect(() => {
    const config = resolvedProductConfigRef.current;

    if (!config || !isProductLandingPersonalizationEnabled) {
      resetPersonalizationState();
      return undefined;
    }

    const { sections, requestedKeys } = extractPersonalizationPayload({
      productConfig: config,
      formatMessage,
    });

    if (sections.length === 0) {
      resetPersonalizationState();
      return undefined;
    }

    let cancelled = false;
    let personalizationTimeoutId;

    setPersonalizationStatus('in_flight');
    setShowPersonalizationComplete(false);

    const timeoutPromise = new Promise((_, reject) => {
      personalizationTimeoutId = setTimeout(
        () => reject(new Error('Product landing personalization timed out')),
        15000,
      );
    });

    const startPersonalization = async () => {
      try {
        const response = await Promise.race([
          requestPersonalization({
            productId,
            pageType: 'product-landing',
            locale,
            businessEntityUuid,
            sections,
          }),
          timeoutPromise,
        ]);

        if (cancelled) {
          return;
        }

        const normalizedSections = normalizePersonalizationResponse(response);
        const { sections: validSections, unknownKeys } = validatePersonalizationResponse(
          normalizedSections,
          requestedKeys,
        );

        if (unknownKeys.length > 0) {
          captureException(
            new ZBError('[product-landing-personalization] Response contained unknown keys', {
              contextData: { details: { unknownKeys: unknownKeys.slice(0, 10) } },
            }),
          );
        }

        const messageMap = validSections.reduce((acc, section) => {
          section.items.forEach((item) => {
            acc[item.key] = item.copy;
          });
          return acc;
        }, {});

        if (Object.keys(messageMap).length === 0) {
          setPersonalizationStatus('fallback');
          setPersonalizedMessageMap((prev) => (Object.keys(prev).length > 0 ? {} : prev));
          return;
        }

        setPersonalizedMessageMap(messageMap);
        setPersonalizationStatus('applied');
        setShowPersonalizationComplete(true);

        clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = setTimeout(() => {
          setShowPersonalizationComplete(false);
        }, 4000);
      } catch (err) {
        if (!cancelled) {
          captureException(new ZBError(err, { message: 'Failed to apply product landing personalization' }));
          setPersonalizationStatus('fallback');
          setPersonalizedMessageMap((prev) => (Object.keys(prev).length > 0 ? {} : prev));
          setShowPersonalizationComplete(false);
        }
      } finally {
        clearTimeout(personalizationTimeoutId);
      }
    };

    startPersonalization();

    return () => {
      cancelled = true;
      clearTimeout(personalizationTimeoutId);
      clearTimeout(completionTimeoutRef.current);
    };
  }, [
    businessEntityUuid,
    captureException,
    formatMessage,
    isProductLandingPersonalizationEnabled,
    locale,
    productId,
    requestPersonalization,
    resetPersonalizationState,
  ]);

  const contextValue = useMemo(
    () => ({
      ...baseContext,
      productConfig: resolvedProductConfig,
      personalizationStatus,
      showPersonalizationComplete,
      isVeloPersonalizedCopy: personalizationStatus === 'applied',
    }),
    [baseContext, resolvedProductConfig, personalizationStatus, showPersonalizationComplete],
  );

  const content = (() => {
    const gateComponents = productConfig?.productGates?.componentGates || [];

    if (!Array.isArray(gateComponents) || gateComponents.length === 0) {
      return children;
    }

    return gateComponents.reduceRight((acc, { Component, props }) => <Component {...props}>{acc}</Component>, children);
  })();

  return (
    <ProductLandingContext.Provider value={contextValue}>
      <ReactIntlProvider locale={locale} messages={mergedMessages}>
        {content}
      </ReactIntlProvider>
    </ProductLandingContext.Provider>
  );
};

ProductLandingProvider.propTypes = {
  children: PropTypes.node.isRequired,
  federated: PropTypes.bool,
  productId: PropTypes.string.isRequired,
};

export { ProductLandingProvider, useProductLandingContext, useProductLandingContextOptional };
